import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  CellValueChangedEvent,
  ColDef,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact, CustomDetailCellRendererProps } from 'ag-grid-react';
import { addDays, format, isValid, parse } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  freshStrawRestApi,
  useGetFreshStrawFertilizationsQuery,
  useRemoveFreshStrawBsFlagMutation,
  useVerifyFreshStrawBillableMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { RowIdParams } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { TOA_GetFreshShipments } from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';
import { useAccessToken } from '@/services/auth';
import { useEditFertilizationsMutation } from '@/services/fertilization';
import { useAutoVerifyIfReconciledMutation } from '@/services/fresh-shipment';
import { freshStrawFertilizationsV2Options } from '@/services/fresh-shipment';
import { useShipmentEstimationQuery } from '@/services/fresh-shipment/hooks';
import { useContextMenu } from '@/utils/context-menu';

import {
  billableShippedColDefs,
  BillableShippedGridContext,
  BillableShippedRow,
} from '../../utils/columnDefs';
import { D5_VALUE } from '../../utils/constants';

export type SelectedFert = {
  row: BillableShippedRow;
  freshShipmentId: string;
};

export type BillableShippedDetailGridParams =
  CustomDetailCellRendererProps<TOA_GetFreshShipments> & {
    selectedFertRef?: MutableRefObject<SelectedFert[]>;
  };

export const BillableShippedDetailGrid = (
  params: BillableShippedDetailGridParams,
) => {
  const token = useAccessToken();
  const { themeMode } = useContext(PrimaryContext);
  const defaultGridOptions = useDefaultGridOptions();
  const dispatch = useDispatch();
  const isAutoVerifyEnabled = useFeatureFlag(
    'Auto_Verify_Reconciled_Billable_Shipped',
  );

  const isDay5AdjustEnabled = useFeatureFlag('Day_5_Adjust_Date');
  const isProgramNameEnabled = useFeatureFlag(
    'Fresh_Shipment_Export_Program_Name',
  );

  const EDITABLE_FIELDS = useMemo(
    () => new Set(['clientTrf', 'clientFrz', 'clientDiscard']),
    [],
  );

  const [searchParams] = useSearchParams();
  const date = searchParams.get('date') ?? '';
  const siteId = searchParams.get('sId') ?? '';
  const freshShipmentId = params.data?.freshShipmentId ?? '';
  const { selectedFertRef } = params;

  const { data: estimation } = useShipmentEstimationQuery({
    freshShipmentId,
    enabled: isDay5AdjustEnabled && !!freshShipmentId,
  });
  const isDay5 = isDay5AdjustEnabled && estimation?.toString() === D5_VALUE;
  const fertilizationDate = useMemo(() => {
    if (!date || !isDay5) return date;
    const fmt =
      date.includes('-') && date.split('-')[0].length === 4
        ? 'yyyy-MM-dd'
        : 'MM-dd-yyyy';
    const d = parse(date, fmt, new Date());
    if (!isValid(d)) return date;
    return format(addDays(d, 1), fmt);
  }, [date, isDay5]);

  const { data: v1Fertilizations = [] } = useGetFreshStrawFertilizationsQuery(
    {
      fertilizationDate,
      fertilizationSite: siteId,
      freshShipmentId,
    },
    { skip: !date || !freshShipmentId || isProgramNameEnabled },
  );

  const { data: v2Fertilizations = [] } = useQuery({
    ...freshStrawFertilizationsV2Options(token, {
      fertilizationDate,
      fertilizationSite: siteId,
      freshShipmentId,
    }),
    enabled: !!token && !!date && !!freshShipmentId && isProgramNameEnabled,
  });

  const fertilizations = isProgramNameEnabled
    ? v2Fertilizations
    : v1Fertilizations;

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<BillableShippedRow>) => {
      const selected = event.api.getSelectedRows();
      if (selectedFertRef) {
        selectedFertRef.current = selected.map((row) => ({
          row,
          freshShipmentId,
        }));
      }
    },
    [freshShipmentId, selectedFertRef],
  );

  const getRowId = useCallback(
    (p: RowIdParams<BillableShippedRow>) => p.data.fertilizationId,
    [],
  );

  const [verifyBillable] = useVerifyFreshStrawBillableMutation();
  const [unverifyBillable] = useRemoveFreshStrawBsFlagMutation();
  const { mutateAsync: editFertilizations } = useEditFertilizationsMutation();
  const { mutateAsync: autoVerifyIfReconciled } =
    useAutoVerifyIfReconciledMutation();

  const gridRef = useRef<AgGridReact>(null);
  const ignoreNextCellChange = useRef(false);
  const [saveStatuses, setSaveStatuses] = useState<
    Record<string, 'saving' | 'saved' | 'error'>
  >({});
  const [pendingEdits, setPendingEdits] = useState<
    Record<string, Partial<BillableShippedRow>>
  >({});
  const [overriddenIds, setOverriddenIds] = useState<Set<string>>(new Set());
  const [unlockedIds, setUnlockedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (fertilizations.length === 0) return;
    const ids = new Set<string>();
    for (const f of fertilizations) {
      const calculated = (f.clientTransfer ?? 0) + (f.clientFrozen ?? 0);
      if (f.billable != null && f.billable !== calculated) {
        ids.add(f.fertilizationId);
      }
    }
    if (ids.size > 0) {
      setOverriddenIds((prev) => {
        const merged = new Set(prev);
        for (const id of ids) merged.add(id);
        return merged;
      });
    }
  }, [fertilizations]);

  const rowData = useMemo<BillableShippedRow[]>(
    () =>
      fertilizations.map((f) => {
        const pending = pendingEdits[f.fertilizationId];
        const clientTrf =
          pending?.clientTrf !== undefined
            ? (pending.clientTrf ?? 0)
            : (f.clientTransfer ?? 0);
        const clientFrz =
          pending?.clientFrz !== undefined
            ? (pending.clientFrz ?? 0)
            : (f.clientFrozen ?? 0);
        const clientDiscard =
          pending?.clientDiscard !== undefined
            ? (pending.clientDiscard ?? 0)
            : (f.clientDiscarded ?? 0);
        const shipClient = f.clientShipped ?? 0;
        const isReconciled =
          clientTrf + clientFrz + clientDiscard === shipClient;
        const reconciliationDiff =
          shipClient - (clientTrf + clientFrz + clientDiscard);

        const isOverridden = overriddenIds.has(f.fertilizationId);
        const calculatedBillable = clientTrf + clientFrz;

        const hasUtilization =
          f.clientTransfer != null ||
          f.clientFrozen != null ||
          f.clientDiscarded != null;
        const billingStatus = f.billableVerified
          ? 'verified'
          : isReconciled
            ? 'reconciled'
            : hasUtilization
              ? 'notReconciled'
              : 'incomplete';

        let billableShipped: number | null;
        if (isOverridden) {
          billableShipped =
            pending?.billableShipped !== undefined
              ? pending.billableShipped
              : (f.billable ?? null);
        } else {
          billableShipped = calculatedBillable;
        }

        return {
          fertilizationId: f.fertilizationId,
          mating: f.mating ?? '',
          shipClient: f.clientShipped ?? null,
          clientTrf:
            pending?.clientTrf !== undefined
              ? pending.clientTrf
              : (f.clientTransfer ?? null),
          clientFrz:
            pending?.clientFrz !== undefined
              ? pending.clientFrz
              : (f.clientFrozen ?? null),
          clientDiscard:
            pending?.clientDiscard !== undefined
              ? pending.clientDiscard
              : (f.clientDiscarded ?? null),
          billableShipped,
          billableShippedOverridden: isOverridden,
          verified: f.billableVerified ?? false,
          isReconciled,
          reconciliationDiff,
          billingStatus,
          saveStatus: saveStatuses[f.fertilizationId],
          sireRegistrationNumber: f.sireRegistrationNumber,
          sireBreedName: f.sireBreedName,
          donorRegistrationNumber: f.donorRegistrationNumber,
          donorBreedName: f.donorBreedName,
          programName: isProgramNameEnabled
            ? ((f as { programName?: string | null }).programName ?? null)
            : null,
          naab: f.naab,
        };
      }),
    [fertilizations, saveStatuses, pendingEdits, overriddenIds],
  );

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<BillableShippedRow>) => {
      if (ignoreNextCellChange.current) {
        ignoreNextCellChange.current = false;
        return;
      }

      const { data, column } = event;
      if (!data) return;
      const { fertilizationId } = data;
      const changedField = column.getColDef().field;

      if (changedField && EDITABLE_FIELDS.has(changedField)) {
        const val = event.newValue as number | null;
        if (val !== null && val !== undefined) {
          if (val < 0) {
            dispatch(
              addToast({
                severity: 'warn',
                summary: 'Invalid value',
                detail: 'Only zero or positive whole numbers are allowed.',
                life: ToastLife.MEDIUM,
              }),
            );
            ignoreNextCellChange.current = true;
            event.node.setDataValue(event.column.getColId(), event.oldValue);
            return;
          }
          if (!Number.isInteger(val)) {
            dispatch(
              addToast({
                severity: 'warn',
                summary: 'Invalid value',
                detail: 'Only whole numbers are allowed.',
                life: ToastLife.MEDIUM,
              }),
            );
            ignoreNextCellChange.current = true;
            event.node.setDataValue(event.column.getColId(), event.oldValue);
            return;
          }
        }
      }

      if (changedField === 'billableShipped') {
        setOverriddenIds((prev) => new Set(prev).add(fertilizationId));
      }

      const isOverridden =
        changedField === 'billableShipped' ||
        overriddenIds.has(fertilizationId);
      const billableShipped = isOverridden
        ? data.billableShipped
        : (data.clientTrf ?? 0) + (data.clientFrz ?? 0);

      const updatedData = { ...data, billableShipped };
      setPendingEdits((prev) => ({ ...prev, [fertilizationId]: updatedData }));
      setSaveStatuses((prev) => ({ ...prev, [fertilizationId]: 'saving' }));
      editFertilizations([
        {
          fertilizationId,
          transferClient: updatedData.clientTrf,
          frozeClient: updatedData.clientFrz,
          discardClient: updatedData.clientDiscard,
          shippedBillable: updatedData.billableShipped,
        },
      ])
        .then(() => {
          setSaveStatuses((prev) => ({ ...prev, [fertilizationId]: 'saved' }));
          dispatch(
            freshStrawRestApi.util.invalidateTags([
              'FertilizationListFreshShipment',
            ]),
          );

          if (isAutoVerifyEnabled && !data.verified) {
            autoVerifyIfReconciled(fertilizationId).then(() => {
              dispatch(
                freshStrawRestApi.util.invalidateTags([
                  'FertilizationListFreshShipment',
                ]),
              );
            });
          }
        })
        .catch(() => {
          setSaveStatuses((prev) => ({ ...prev, [fertilizationId]: 'error' }));

          if (changedField === 'billableShipped') {
            setOverriddenIds((prev) => {
              const next = new Set(prev);
              next.delete(fertilizationId);
              return next;
            });
          }
        });
    },
    [
      editFertilizations,
      autoVerifyIfReconciled,
      overriddenIds,
      dispatch,
      EDITABLE_FIELDS,
      isAutoVerifyEnabled,
    ],
  );

  const onVerifyToggle = useCallback(
    (fertilizationId: string, currentlyVerified: boolean) => {
      if (currentlyVerified) {
        (params.node.data as Record<string, unknown>).verified = false;
        unverifyBillable({ fertilizationId });
      } else {
        verifyBillable([fertilizationId]);
      }
    },
    [verifyBillable, unverifyBillable, params.node],
  );

  const onLockToggle = useCallback((fertilizationId: string) => {
    setUnlockedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fertilizationId)) {
        next.delete(fertilizationId);
      } else {
        next.add(fertilizationId);
      }
      return next;
    });
    setTimeout(() => {
      gridRef.current?.api?.refreshCells({ force: true });
    }, 0);
  }, []);

  const gridContext = useMemo<BillableShippedGridContext>(
    () => ({ onVerifyToggle, onLockToggle, unlockedIds }),
    [onVerifyToggle, onLockToggle, unlockedIds],
  );

  const detailDefaultColDef: ColDef = useMemo(
    () => ({
      ...defaultColDef,
      sortable: true,
      resizable: true,
    }),
    [],
  );

  const { getContextMenuItems: billableShippedContextMenu } = useContextMenu({
    idKey: 'fertilizationId',
  });

  return (
    <div className="p-4">
      <p className="mb-3 text-sm font-semibold">Embryos</p>
      <div
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} w-full`}
      >
        <WithLocalStorage storageKey="billableShippedGrid">
          <AgGridReact<BillableShippedRow>
            ref={gridRef}
            className="h-full w-full"
            gridOptions={defaultGridOptions}
            getContextMenuItems={billableShippedContextMenu}
            columnDefs={billableShippedColDefs}
            defaultColDef={detailDefaultColDef}
            rowData={rowData}
            headerHeight={32}
            rowHeight={28}
            getRowId={getRowId}
            stopEditingWhenCellsLoseFocus={true}
            domLayout="autoHeight"
            context={gridContext}
            rowSelection="multiple"
            onCellValueChanged={onCellValueChanged}
            onSelectionChanged={onSelectionChanged}
          />
        </WithLocalStorage>
      </div>
    </div>
  );
};
